import { ChangeSet } from "prosemirror-changeset";
import { type Attrs } from "prosemirror-model";
import { type StepMap } from "prosemirror-transform";

import { type LoggerInterface, getTimer } from "@moment/utils";

import { type DocumentValidator } from "../checksum";
import { Plugin, PluginKey } from "../prosemirror-state";

export const nodeChecksumPlugin = (
	logger: LoggerInterface,
	validator: DocumentValidator
): Plugin => {
	return new Plugin({
		key: new PluginKey("nodeChecksumPlugin"),
		appendTransaction: (transactions, prevState, nextState) => {
			const timer = getTimer(logger);
			let hasAnyUpdates = false;
			const tr = nextState.tr;
			const changeSet = timer.timeFunction("plugin_changeset_create", () => {
				const initialChangeSet = ChangeSet.create(prevState.doc);
				const stepMaps: StepMap[] = [];
				for (const txn of transactions.filter((txn) => txn.docChanged)) {
					stepMaps.push(...txn.steps.map((step) => step.getMap()));
				}
				// ChangeSet.addSteps() internally compares its starting document
				// with the input document in order to compute a diff:
				// See: https://github.com/ProseMirror/prosemirror-changeset/blob/2.2.1/src/changeset.ts#L32-L45
				return initialChangeSet.addSteps(nextState.doc, stepMaps, []);
			});

			// Keep track of the updated node positions since the new editor state doesn't contain any
			// of the updated node attrs until this returns. This array is used to remember the
			// updated nodes and avoid flagging these nodes for not having a computed checksum in its nodeAttr.
			const updatedNodePositions: number[] = [];
			timer.timeFunction("plugin_changeset_checksum_compute", () => {
				// The resulting ChangeSet contains the information of insertions/deletions for the transactions
				// Each change contains the oldStart/oldEnd + newStart/newEnd positions
				for (const change of changeSet.changes) {
					// Identify the nodes which this change touches
					// Using the fromB and toB here since this is the resulting doc after the changes
					const start = change.fromB;
					const end = change.toB;
					// Get the nodes between the change positions.
					nextState.doc.nodesBetween(start, end, (node, pos) => {
						const nodeChecksum = validator.checksum(node);
						// Update the attrs with a checksum for the node if they differ.
						// If they are the same, that probably means the node was just moved around in the document with
						// no content changes. If this was the case, a new transaction is not needed since no attrs were
						// updated.
						// If the checksum algorithm changes, the checksum version would differ and it would initiate
						// a new checksum for the node(s).
						if (
							node.attrs["checksum_version"] !== nodeChecksum.version ||
							node.attrs["checksum_hash"] !== nodeChecksum.hash
						) {
							updatedNodePositions.push(pos);
							let newAttrs: Attrs = {
								...node.attrs,
								checksum_version: nodeChecksum.version,
								checksum_hash: nodeChecksum.hash,
							};
							if (validator.checksumDebuggingEnabled()) {
								newAttrs = {
									...newAttrs,
									checksum_debug: nodeChecksum.extras?.debug,
								};
							}
							hasAnyUpdates = true;
							try {
								tr.setNodeMarkup(pos, undefined, newAttrs);
							} catch (error) {
								logger.error(
									"error on setNodeMarkup() for checksums. suppressing error.",
									{
										error,
										node,
										tr,
										transactions,
									}
								);
							}
						}
						// Return false to avoid traversing down past the direct descendants of the doc
						return false;
					});
				}
			});
			if (validator.checksumDebuggingEnabled()) {
				nextState.doc.descendants((node, pos) => {
					if (updatedNodePositions.find((position) => position === pos)) {
						// Skip the current updated checksums since it is safe to assume that these got updated.
						return false;
					}
					if (!node.attrs["checksum_version"] || !node.attrs["checksum_hash"]) {
						const parsedNodeForChecksum = validator.parseNode(node);
						// Found checksums which didn't get computed for a node.
						// This log is used to detect if there exists a flaw in using the change set to
						// determine the changed nodes. If a single log is present, that likely means that
						// the change set logic could have an off by one issue. It could also appear many times
						// as the transaction array is used to compute the change sets.
						logger.debug("found node without a computed checksum in checksum plugin", {
							parsedNodeForChecksum,
							pos,
						});
					}
					return false;
				});
			}

			// Return the transaction if there was a checksum update.
			// Otherwise, return null to avoid unnecessarily creating a new transaction.
			return hasAnyUpdates ? tr : null;
		},
	});
};
